import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  UserIcon,
  ClockIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChatBubbleBottomCenterTextIcon,
  EyeIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import {
  useOrderTrackStore,
  OrderDetail,
  AdminNote,
  EmployeeNote,
  VoiceNote,
  RemarkEntry,
} from "../store/OrderStore";
import axiosApi from "../server/service";
import toast from "react-hot-toast";
import EmployeeCard, { Employee } from "../component/EmployeeSelector";
import { stringify } from "qs";
import VoiceNoteRecorder from "../component/VoiceRecorder";
import { format } from "date-fns";
import GroupWorkProgressModal from "../component/GroupWorkProgressModal";

// Helper to get the populated employee object from an assignment
const getEmployeeFromAssignment = (assignment: any) => {
  return (
    assignment.masterId ||
    assignment.jobWorkId ||
    assignment.karigarId ||
    assignment.groupWorkId
  );
};

const EmployeeRemarkHistory: React.FC<{ remarks: RemarkEntry[]; employeeName: string }> = ({ remarks, employeeName }) => {

    const sortedRemarks = remarks
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h5 className="text-sm font-bold text-gray-700 mb-2">
                {employeeName}'s Progress Notes ({sortedRemarks.length})
            </h5>
            <div className="max-h-40 overflow-y-auto pr-2 space-y-3">
                {sortedRemarks.length === 0 ? (
                    <p className="text-sm text-gray-500 italic text-center py-2">No remarks found for this employee.</p>
                ) : (
                    sortedRemarks.map((entry) => (
                        <div key={entry._id} className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                            <p className="text-xs font-semibold text-gray-800 mb-1 leading-snug whitespace-pre-wrap break-words">
                                {entry.text}
                            </p>
                            <p className="text-xs text-gray-500 text-right">
                                {format(new Date(entry.createdAt), 'dd MMM, hh:mm a')}
                            </p>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

interface AssignmentStepProps {
  title: string;
  stepNumber: number;
  employeeTypes: ("Master" | "Karigar" | "job-work" | "Group Work")[];
  onReload?: () => void;
}

const AssignmentStep: React.FC<AssignmentStepProps> = ({
  title,
  stepNumber,
  employeeTypes,
  onReload,
}) => {


  const [selectedEmployeeRemarks, setSelectedEmployeeRemarks] = useState<RemarkEntry[]>([]);
  const [selectedEmployeeForRemark, setSelectedEmployeeForRemark] = useState<{ id: string, name: string, type: string } | null>(null);

  const [isProgressModalOpen, setProgressModalOpen] = useState(false);
  const [selectedGroupForModal, setSelectedGroupForModal] = useState<any>(null);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );

  const orderDetails = useOrderTrackStore((state) => state.orderDetails);
  const { orderId, assignEmployeeToSegments, loadOrder } = useOrderTrackStore();

  const adminNotes = useOrderTrackStore((state) => state.adminNotes);

  const employeeNotes = useOrderTrackStore((state) => state.employeeNotes);

  const [selectedEmployeeNotes, setSelectedEmployeeNotes] = useState<
    VoiceNote[]
  >([]);

  const [viewingNotesFor, setViewingNotesFor] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [notesToDisplay, setNotesToDisplay] = useState<VoiceNote[]>([]);

  const [remarks, setRemarks] = useState("");
  const [selectedSegmentDetailIds, setSelectedSegmentDetailIds] = useState<
    string[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUserIdsForNote, setSelectedUserIdsForNote] = useState<
    string[]
  >([]);


  const getEmployeeRemarksForType = useCallback((employeeId: string, employeeType: string): RemarkEntry[] => {
      let remarks: RemarkEntry[] = [];
      const remarkKey = employeeType === "Master" ? "masterRemark" : employeeType === "job-work" ? "jobWorkRemark" : null;

      const assignmentKey = employeeType === "Master" ? 'masterAssignments' : employeeType === "job-work" ? 'jobWorkAssignments' : null;


      if (!remarkKey || !assignmentKey) return [];

      orderDetails.forEach(detail => {

          const assignmentArray = detail[assignmentKey] as any[] | undefined;
          
          if (assignmentArray) {
              assignmentArray.forEach(assignment => {
                  const employee = getEmployeeFromAssignment(assignment);
                  if (employee?._id === employeeId) {

                      remarks = [...remarks, ...(assignment[remarkKey] || [])];
                  }
              });
          }
      });
      return remarks; 
  }, [orderDetails]);

  

  const handleViewProgressClick = (groupEmployee: any) => {
    setSelectedGroupForModal(groupEmployee);
    setProgressModalOpen(true);
  };

  const filteredAdminNotes = useMemo(() => {
    if (!adminNotes) return [];
    return adminNotes.filter(
      (noteGroup) =>
        noteGroup.userId &&
        employeeTypes.includes(noteGroup.userId.employeeType as any)
    );
  }, [adminNotes, employeeTypes]);

  const getAllAssignmentsForDetail = (detail: OrderDetail) => {
    let assignments: any[] = [];
    if (employeeTypes.includes("Master")) {
      assignments = [...assignments, ...(detail.masterAssignments || [])];
    }
    if (employeeTypes.includes("job-work")) {
      assignments = [...assignments, ...(detail.jobWorkAssignments || [])];
    }
    if (employeeTypes.includes("Karigar")) {
      assignments = [...assignments, ...(detail.karigarAssignments || [])];
    }
    if (employeeTypes.includes("Group Work")) {
      assignments.push(...(detail.groupWorkAssignments || []));
    }
    return assignments;
  };

  const isEmployeeAssignedToAnySegment = (employeeId: string) => {
    return orderDetails.some((detail) =>
      getAllAssignmentsForDetail(detail).some(
        (a: any) => getEmployeeFromAssignment(a)?._id === employeeId
      )
    );
  };

  const filteredEmployees = employees.filter((emp) =>
    emp.fullName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchAllEmployees = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const response = await axiosApi.get("/api/users/by-type", {
        params: { type: employeeTypes, orderId },
        paramsSerializer: (params) =>
          stringify(params, { arrayFormat: "repeat" }),
      });
      setEmployees(response.data.users || []);
    } catch (error) {
      toast.error("Could not fetch available employees.");
    } finally {
      setLoading(false);
    }
  }, [orderId, employeeTypes]);

  useEffect(() => {
    fetchAllEmployees();
  }, [fetchAllEmployees]);

  const handleSelectEmployee = (employeeId: string) => {
    setSelectedEmployeeId(employeeId);

    setViewingNotesFor(null);
    setNotesToDisplay([]);

    setSelectedEmployeeRemarks([]);
    setSelectedEmployeeForRemark(null);

    const segmentsForThisEmployee = orderDetails
      .filter((detail) =>
        getAllAssignmentsForDetail(detail).some(
          (a: any) => getEmployeeFromAssignment(a)?._id === employeeId
        )
      )
      .map((d) => d._id);
    setSelectedSegmentDetailIds(segmentsForThisEmployee);

    if (segmentsForThisEmployee.length > 0) {
      const firstDetail = orderDetails.find(
        (d) => d._id === segmentsForThisEmployee[0]
      );
      if (firstDetail) {
        const assignment = getAllAssignmentsForDetail(firstDetail).find(
          (a: any) => getEmployeeFromAssignment(a)?._id === employeeId
        );
        setRemarks(assignment?.remarks || "");
      }
    } else {
      setRemarks("");
    }
  };

  const handleSelectUserForNote = (employee: Employee) => {
    // Toggle functionality for selectedUserIdsForNote
    setSelectedUserIdsForNote((prev) =>
      prev.includes(employee._id)
        ? prev.filter((id) => id !== employee._id)
        : [...prev, employee._id]
    );
    
    // Logic for displaying Remarks
    if (selectedEmployeeForRemark?.id === employee._id) {
        // If clicking the currently selected employee, close the remark view
        setSelectedEmployeeForRemark(null);
        setSelectedEmployeeRemarks([]);
    } else {
        // If clicking a new employee
        const isRemarkEmployee = employee.employeeType === "Master" || employee.employeeType === "job-work";
        
        if (isRemarkEmployee) {
            const remarks = getEmployeeRemarksForType(employee._id, employee.employeeType);
            setSelectedEmployeeRemarks(remarks);
            setSelectedEmployeeForRemark({ id: employee._id, name: employee.fullName, type: employee.employeeType });
        } else {
            // Clear remarks if the selected employee is not Master/Job Work
            setSelectedEmployeeRemarks([]);
            setSelectedEmployeeForRemark({ id: employee._id, name: employee.fullName, type: employee.employeeType });
        }
    }

    // Toggle voice notes view as before
    handleViewNotesClick(employee);
  };

  const handleSegmentChange = (segmentDetailId: string) => {
    setSelectedSegmentDetailIds((prev) =>
      prev.includes(segmentDetailId)
        ? prev.filter((id) => id !== segmentDetailId)
        : [...prev, segmentDetailId]
    );
  };

  const handleViewNotesClick = (employee: Employee) => {
    // If we are clicking the same person whose notes are already open, close the view.
    if (viewingNotesFor?.id === employee._id) {
      setViewingNotesFor(null);
      setNotesToDisplay([]);
    } else {
      // Otherwise, open this person's notes.
      setViewingNotesFor({ id: employee._id, name: employee.fullName });
      const notesForEmployee = employeeNotes.find(
        (noteGroup) => noteGroup.userId?._id === employee._id
      );
      setNotesToDisplay(notesForEmployee ? notesForEmployee.notes : []);
    }
  };

  const handleAssign = () => {
    if (!selectedEmployeeId) {
      toast.error("Please select an employee.");
      return;
    }
    const selectedEmployee = employees.find(
      (e) => e._id === selectedEmployeeId
    );
    if (!selectedEmployee) {
      toast.error("Could not find employee details.");
      return;
    }
    assignEmployeeToSegments(
      selectedEmployee._id,
      selectedEmployee.fullName,
      selectedEmployee.employeeType,
      remarks,
      selectedSegmentDetailIds
    );
  };

  // const handleSelectUserForNote = (userId: string) => {
  //   setSelectedUserIdsForNote((prev) =>
  //     prev.includes(userId)
  //       ? prev.filter((id) => id !== userId)
  //       : [...prev, userId]
  //   );
  // };

  const uniqueAssignedEmployees = useMemo(() => {
    const allAssigned = new Map<string, Employee>();
    orderDetails.forEach((detail) => {
      const assignments = getAllAssignmentsForDetail(detail);
      assignments.forEach((assignment) => {
        const employee = getEmployeeFromAssignment(assignment);
        if (employee && employee._id && !allAssigned.has(employee._id)) {
          allAssigned.set(employee._id, {
            _id: employee._id,
            fullName: employee.fullName,
            employeeType: employee.employeeType,
          } as Employee);
        }
      });
    });
    return Array.from(allAssigned.values());
  }, [orderDetails, employeeTypes]);

  const handleAdminNoteUpload = useCallback(
    async (audioBlob: Blob) => {
      if (!audioBlob || selectedUserIdsForNote.length === 0) {
        toast.error("Please record a note and select at least one employee.");
        return Promise.reject("No audio or user selected");
      }
      const toastId = toast.loading(`Sending voice note...`);
      const formData = new FormData();
      formData.append("audio", audioBlob, `admin-note-order-${orderId}.webm`);
      selectedUserIdsForNote.forEach((id) => formData.append("userIds", id));

      try {
        const response = await axiosApi.post(
          `/api/orders/${orderId}/add-admin-note`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Voice note sent successfully!", { id: toastId });
        if (response.data?.data) {
          loadOrder(response.data.data);
        }
        setSelectedUserIdsForNote([]);
      } catch (err: any) {
        toast.error(err.response?.data?.message || "Failed to send note.", {
          id: toastId,
        });
        throw err;
      }
    },
    [orderId, selectedUserIdsForNote, loadOrder]
  );

  const selectedEmployeeDetails = employees.find(
    (e) => e._id === selectedEmployeeId
  );

  const emptyNotesArray = useMemo(() => [], []);
  const noOpOnUpdate = useCallback(() => {}, []);

  console.log("AssignmentStep selectedEmployeeNotes:", selectedEmployeeNotes);

  console.log("AssignmentStep employeeNotes from store:", employeeNotes);

  const assignedGroupWorkUsers = useMemo(() => {
    const groups = new Map<string, Employee>();
    // Hum sirf tabhi groups nikalenge jab is step mein "Group Work" include ho
    if (employeeTypes.includes("Group Work")) {
      orderDetails.forEach((detail) => {
        detail.groupWorkAssignments?.forEach((assignment) => {
          const employee = getEmployeeFromAssignment(assignment);
          if (employee && employee._id && !groups.has(employee._id)) {
            groups.set(employee._id, employee as Employee);
          }
        });
      });
    }
    return Array.from(groups.values());
  }, [orderDetails, employeeTypes]);

  // const getEmployeeRemarks = (
  //   assignment: any,
  //   employeeType: string
  // ): RemarkEntry[] => {
  //   if (employeeType === "Master") {
  //     return assignment.masterRemark || [];
  //   }
  //   if (employeeType === "job-work") {
  //     return assignment.jobWorkRemark || [];
  //   }
  //   return [];
  // };



  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-8">
      {/* Employee Assignment Section */}
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
          {stepNumber}
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 font-medium">
            Assign specific sizes/segments to available workers.
          </p>
        </div>
      </div>
      <div className="mb-8">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
          <UserIcon className="h-5 w-5 mr-2 text-indigo-600" />
          Available Employees (
          {employeeTypes
            .join(" / ")
            .replace("job-work", "Job Work")
            .replace("Group Work", "Group Work")}
          )
        </h3>
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search employee by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200"
          />
        </div>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 max-h-[300px] overflow-y-auto pr-2">
          {loading ? (
            <p className="col-span-full text-center text-gray-500 py-6">
              Loading...
            </p>
          ) : (
            filteredEmployees.map((employee) => (
              <EmployeeCard
                key={employee._id}
                employee={employee}
                onSelect={handleSelectEmployee as any}
                isSelected={selectedEmployeeId === employee._id}
                isAssigned={isEmployeeAssignedToAnySegment(employee._id)}
              />
            ))
          )}
          {!loading && employees.length === 0 && (
            <p className="col-span-full text-center text-gray-500 py-6">
              No employees found.
            </p>
          )}
        </div>
      </div>

      {/* Assignment Form Section */}
      {selectedEmployeeId && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 space-y-6 mt-6 animate-fade-in shadow-sm">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Assigning To:
            </label>
            <div className="w-full p-3 bg-slate-100 border border-slate-300 rounded-lg text-slate-800 font-medium">
              {selectedEmployeeDetails?.fullName} (
              {selectedEmployeeDetails?.employeeType})
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">
              Select which sizes to assign/re-assign to this person:
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[200px] overflow-y-auto p-2 border border-slate-200 rounded-lg">
              {orderDetails.map((detail) => (
                <div key={detail._id}>
                  <input
                    type="checkbox"
                    id={`segment-${detail._id}`}
                    value={detail._id}
                    checked={selectedSegmentDetailIds.includes(detail._id)}
                    onChange={() => handleSegmentChange(detail._id)}
                    className="hidden peer"
                  />
                  <label
                    htmlFor={`segment-${detail._id}`}
                    className="block w-3/5 py-3 text-center text-sm font-medium text-gray-800 bg-white border border-gray-500 rounded-lg cursor-pointer transition-all duration-200 ease-in-out hover:border-indigo-500 hover:shadow-sm peer-checked:border-indigo-600 peer-checked:bg-indigo-50 peer-checked:ring-2 peer-checked:ring-indigo-200 peer-checked:text-indigo-800"
                  >
                    {detail.sizeSegmentId.segmentName}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="remarks"
              className="block text-sm font-semibold text-gray-800 mb-1.5"
            >
              Remarks / Notes{" "}
              <span className="text-gray-400 font-normal">
                (This will be saved to all selected segments)
              </span>
            </label>
            <textarea
              id="remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              placeholder="Add any special instructions..."
              className="w-full p-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-colors duration-200"
            />
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={handleAssign}
              disabled={selectedSegmentDetailIds.length === 0}
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/30 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:from-indigo-500 disabled:to-purple-500"
            >
              <UserPlusIcon className="h-5 w-5" />
              <span>
                Confirm Assignment ({selectedSegmentDetailIds.length})
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Current Assignment Status Table */}
      <div className="mb-8 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <ClockIcon className="h-6 w-6 mr-3 text-indigo-600" />
          Current Assignment Status
        </h3>
        <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <th className="px-6 py-4 text-left font-semibold text-gray-900 text-sm tracking-wide">
                  Size Segment
                </th>
                <th className="px-6 py-4 text-left font-semibold text-gray-900 text-sm tracking-wide">
                  Assigned To
                </th>
                <th className="px-6 py-4 text-left font-semibold text-gray-900 text-sm tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {orderDetails.map((detail) => {
                const assignments = getAllAssignmentsForDetail(detail);
                return (
                  <tr
                    key={detail._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 font-semibold text-gray-900 text-base">
                      {detail.sizeSegmentId.segmentName}
                    </td>
                    <td className="px-6 py-4">
                      {assignments.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          {assignments.map((assignment: any) => {
                            const employee =
                              getEmployeeFromAssignment(assignment);
                            return (
                              <div
                                key={assignment._id}
                                className="font-semibold text-gray-800 text-sm"
                              >
                                <div className="font-semibold text-gray-800 text-sm">
                                  {employee ? employee.fullName : "..."}
                                  <span className="text-xs text-gray-500 ml-2 font-medium capitalize">
                                    (
                                    {employee?.employeeType?.replace(
                                      "-",
                                      " "
                                    ) || "..."}
                                    )
                                  </span>
                                </div>

                                {/* {employee && employee.employeeType === "Group Work" && (
                                  <button
                                    onClick={() => handleViewProgressClick(employee)}
                                    className="flex items-center gap-1 text-xs text-indigo-600 font-semibold hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-md transition"
                                  >
                                    <EyeIcon className="w-4 h-4" />
                                    Progress
                                  </button>
                                )} */}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic text-sm">
                          Not Assigned
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2 items-start">
                        {assignments.length > 0 ? (
                          assignments.map((assignment, index) => (
                            <span
                              key={assignment._id || index}
                              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide ${
                                assignment.workStatus === "completed"
                                  ? "bg-green-50 text-green-700 border border-green-200"
                                  : assignment.workStatus === "incomplete"
                                  ? "bg-red-50 text-red-700 border border-red-200"
                                  : "bg-blue-50 text-blue-700 border border-blue-200"
                              }`}
                            >
                              {assignment.workStatus === "completed"
                                ? "Completed"
                                : assignment.workStatus === "incomplete"
                                ? "Incomplete"
                                : assignment.workStatus === "in progress"
                                ? "In Progress"
                                : ""}
                            </span>
                          ))
                        ) : (
                          <span className="bg-gray-50 text-gray-600 border border-gray-200 inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide">
                            Pending
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {assignedGroupWorkUsers.length > 0 && (
        <div className="mb-8 mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <UserGroupIcon className="h-6 w-6 mr-3 text-indigo-600" />
            Group Work Progress
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Click on a group name to view their detailed progress report.
          </p>
          <div className="flex flex-wrap gap-3">
            {assignedGroupWorkUsers.map((group) => (
              <button
                key={group._id}
                onClick={() => handleViewProgressClick(group)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 font-semibold rounded-lg hover:bg-indigo-100 hover:text-indigo-800 transition-all duration-200 shadow-sm border border-indigo-100"
              >
                <EyeIcon className="w-5 h-5" />
                {group.fullName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* General Admin Note Section */}
      <div className="space-y-6 pt-8 border-t border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 flex items-center">
          <ChatBubbleBottomCenterTextIcon className="h-6 w-6 mr-3 text-indigo-600" />
          Send voice Note to Assigned Employees
        </h3>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-4">
                1. Select Employee to SEND a new note:
              </label>
              {uniqueAssignedEmployees.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {uniqueAssignedEmployees.map((employee) => {
                    const isSelectedForSending =
                      selectedUserIdsForNote.includes(employee._id);
                    return (
                      <button
                        key={employee._id}
                        onClick={() => handleSelectUserForNote(employee)}
                        // onClick={() => {
                        //   handleViewNotesClick(employee);
                        //   handleSelectUserForNote(employee._id);
                        // }}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 border-2 shadow-sm ${
                          isSelectedForSending
                            ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-offset-2 ring-indigo-500"
                            : "bg-white text-gray-800 border-gray-300 hover:border-indigo-500"
                        }`}
                      >
                        {employee.fullName}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  No employees have been assigned to this order yet.
                </p>
              )}
            </div>
          </div>
        </div>

        {selectedUserIdsForNote.length > 0 && (
          <div className="bg-white border border-indigo-200/60 rounded-xl shadow-inner p-6 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <h4 className="text-base font-semibold text-indigo-800">
                2. Record and Send Note ({selectedUserIdsForNote.length}{" "}
                Selected)
              </h4>
              <button
                onClick={() => setSelectedUserIdsForNote([])}
                className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <VoiceNoteRecorder
              key={`admin-note-${selectedUserIdsForNote.join("-")}`}
              onUpload={handleAdminNoteUpload}
              existingNotes={emptyNotesArray}
              onUpdate={noOpOnUpdate}
            />
          </div>
        )}


        {selectedEmployeeForRemark && 
          (selectedEmployeeForRemark.type === "Master" || selectedEmployeeForRemark.type === "job-work") && (
            <div className="mt-4 pt-4 border-t border-gray-200 animate-fade-in">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <EyeIcon className="h-6 w-6 mr-3 text-purple-600" />
                {selectedEmployeeForRemark.name}'s Assigned Remarks ({selectedEmployeeForRemark.type})
              </h3>
              <EmployeeRemarkHistory 
                remarks={selectedEmployeeRemarks} 
                employeeName={selectedEmployeeForRemark.name}
              />
            </div>
        )}

        <div className="space-y-3 pt-4">
          <h4 className="text-base font-semibold text-gray-700">
            Admin Voice notes
          </h4>
          {filteredAdminNotes && filteredAdminNotes.length > 0 ? (
            <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
              {/* ✅ CHANGE: Updated rendering logic for grouped notes */}
              {filteredAdminNotes.map((noteGroup: AdminNote) => (
                <div
                  key={noteGroup._id}
                  className="bg-gray-50 p-3 rounded-lg border border-gray-200"
                >
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-bold text-gray-800">
                      To: {noteGroup.userId?.fullName || "Unknown User"}
                    </p>
                  </div>

                  {/* Inner loop to display all notes for this user */}
                  <div className="space-y-3 mt-2">
                    {noteGroup.notes.map((note) => (
                      <div
                        key={note._id}
                        className="pl-4 border-l-2 border-gray-300"
                      >
                        <p className="text-xs text-gray-500 mb-1">
                          {format(
                            new Date(note.uploadedAt),
                            "dd MMM yyyy, h:mm a"
                          )}
                        </p>
                        <VoiceNoteRecorder
                          key={note._id}
                          existingNotes={[note]} // Pass single note as expected by the component
                          isReadOnly={true}
                          onUpdate={noOpOnUpdate}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No notes have been sent for this order yet.
            </p>
          )}
        </div>

        {viewingNotesFor && (
          <div className="mt-4 pt-4 border-t border-gray-200 animate-fade-in">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <ChatBubbleBottomCenterTextIcon className="h-6 w-6 mr-3 text-green-600" />
              Viewing Voice Notes from {viewingNotesFor.name}
            </h3>
            <div className="bg-green-50/50 border border-green-200 rounded-xl p-6">
              {notesToDisplay.length > 0 ? (
                <VoiceNoteRecorder
                  key={viewingNotesFor.id}
                  existingNotes={notesToDisplay}
                  isReadOnly={true}
                  onUpdate={noOpOnUpdate}
                />
              ) : (
                <p className="text-sm text-gray-500 italic text-center">
                  No voice notes found from this user for this order.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {selectedGroupForModal && (
        <GroupWorkProgressModal
          isOpen={isProgressModalOpen}
          onClose={() => setProgressModalOpen(false)}
          group={selectedGroupForModal}
          orderDetails={orderDetails}
        />
      )}
    </div>
  );
};

export default AssignmentStep;
